import { ApolloError, DocumentNode } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { PopperContent } from '@/components/poppers/PopperContent';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  ClientProfileFormShape,
  ClientProfileNameFields,
  getDefaultClientProfileFormValues,
} from '@/modules/clientProfiles/ClientProfile.types';
import { MergeClientProfilesAction } from '@/modules/clientProfiles/MergeClientProfiles/MergeClientProfilesAction';
import { FormSideDrawer } from '@/modules/common/SideDrawer/FormSideDrawer';
import { useCreateClientProfileMutation } from '@/modules/forms/generic/graphql/CreateClientProfile.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ClientProfileRelationshipType } from '@/types/schema';
import { getIndefiniteArticle } from '@/utils/formatting/strings';

import {
  ClientProfileSideDrawerContext,
  ClientProfileSideDrawerContextProvider,
} from './ClientProfileModal.context';
import { ClientProfilePerson } from './ClientProfileModal.UI';
import {
  mapDataToForm,
  mapFormToCreatePayload,
  mapFormToUpdatePayload,
} from './ClientProfileModal.utils';
import { useDeleteClientProfileMutation } from './graphql/ClearClientProfileModal.generated';
import { useBeneficiaryFormDataQuery } from './graphql/ClientProfileFormData.generated';
import { useUpdateClientProfileModalMutation } from './graphql/UpdateClientProfileModal.generated';

export interface ClientProfileSideDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  householdId: string;
  clientProfileId: string | null;
  isPrimary?: boolean;
  allowDelete?: boolean;
  clientProfileTypeDisplay: string;
  forceGrantorIfNoClientProfileId?: boolean;
  existingPrimaryClientId?: string | null;
  onAfterCreate?: (newId: string) => void;
  /** Called after a successful create/update/delete (not on cancel/close). */
  onAfterSave?: () => void;
  initialClientProfileName?: ClientProfileNameFields;
  initialRelationships?: Record<string, ClientProfileRelationshipType>;
  ignoredQueryDocuments?: DocumentNode[];
  createdFromSuggestionID?: string;
  hideRelationshipInput?: boolean;
}

function ClientProfileSideDrawerInner({
  isOpen,
  setIsOpen,
  clientProfileTypeDisplay,
  forceGrantorIfNoClientProfileId: forceGrantor,
  allowDelete = true,
  onAfterCreate,
  onAfterSave,
  initialClientProfileName,
  initialRelationships,
  existingPrimaryClientId,
  ignoredQueryDocuments,
  createdFromSuggestionID,
  hideRelationshipInput = false,
}: ClientProfileSideDrawerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const canEdit = useCanEditHouseholdData();
  const { isCreate, householdId, clientProfileId, isPrimary } = useContext(
    ClientProfileSideDrawerContext
  );
  const { primaryClients } = useHouseholdDetailsContext();
  const { data, loading: dataFetchLoading } = useBeneficiaryFormDataQuery({
    variables: { where: { id: householdId } },
    onError: (error) => {
      showFeedback('Could not fetch user data.  Please try again');
      reportError('Caught error when fetching user data', error);
    },
  });

  const formMethods = useForm<ClientProfileFormShape>({
    defaultValues: getDefaultClientProfileFormValues({
      relationshipOptions: [],
      householdId: '',
      initialClientProfileName,
      hasGrantorEnabled: isCreate ? (forceGrantor ?? false) : false,
    }),
  });
  const { reset, handleSubmit, formState, getValues, shouldBlockNavigation } =
    formMethods;

  useEffect(() => {
    if (data) {
      let relationships = initialRelationships;
      if (existingPrimaryClientId && !clientProfileId && isPrimary) {
        relationships = {
          ...initialRelationships,
          [existingPrimaryClientId]: ClientProfileRelationshipType.Spouse,
        };
      }

      const newFormData = mapDataToForm({
        data,
        clientProfileId,
        initialRelationships: relationships,
      });
      // If adding a new profile, keep the existing grantor flag and name fields.
      if (!clientProfileId) {
        newFormData.isGrantor = getValues('isGrantor');
        newFormData.isPrimary = isPrimary;
        newFormData.firstName = getValues('firstName');
        newFormData.middleName = getValues('middleName');
        newFormData.lastName = getValues('lastName');
        newFormData.suffix = getValues('suffix');
        newFormData.notes = getValues('notes');
        newFormData.isDeceased = getValues('isDeceased');
        newFormData.dateOfDeath = getValues('dateOfDeath');

        // When adding a second primary client, auto-populate the state from the
        // existing primary client so tax calculations use the correct state.
        if (existingPrimaryClientId && isPrimary) {
          const existingPrimaryClient = primaryClients?.find(
            (client) => client.id === existingPrimaryClientId
          );
          if (existingPrimaryClient?.stateCode) {
            newFormData.address = {
              ...newFormData.address,
              stateCode: existingPrimaryClient.stateCode,
            };
          }
        }
      }
      reset(newFormData);
    }
  }, [
    clientProfileId,
    data,
    existingPrimaryClientId,
    getValues,
    initialRelationships,
    isPrimary,
    primaryClients,
    reset,
  ]);

  const [createClientProfile, { loading: createLoading }] =
    useCreateClientProfileMutation({
      onCompleted: ({ createClientProfile }) => {
        showFeedback(`Created ${clientProfileTypeDisplay} successfully`, {
          variant: 'success',
        });
        onAfterCreate?.(createClientProfile.id);
        onAfterSave?.();
        setIsOpen(false);
      },
      onError: (error: ApolloError) => {
        showFeedback(
          `Encountered an error creating the ${clientProfileTypeDisplay}.  Please try again.`
        );
        reportError('Encountered error when creating client profile', error);
      },
      onQueryUpdated: (query) => {
        return shouldRefetchQuery(query.queryName, {
          ignoredQueryDocuments,
        });
      },
      refetchQueries: 'active',
      awaitRefetchQueries: true,
    });

  const [deleteClientProfile, { loading: deleteLoading }] =
    useDeleteClientProfileMutation({
      onCompleted: () => {
        onAfterSave?.();
        setIsOpen(false);
        showFeedback(`Deleted individual successfully`, {
          variant: 'success',
        });
      },
      onError: (err) => {
        const errorMessages = getUserFacingErrorMessages(err);
        showFeedback(
          `Failed to delete individual: ${errorMessages.join(', ')}`
        );
      },
      onQueryUpdated: (query) => {
        return shouldRefetchQuery(query.queryName, {
          ignoredQueryDocuments,
        });
      },
      refetchQueries: 'active',
      awaitRefetchQueries: true,
    });

  const [updateClientProfile, { loading: updateLoading }] =
    useUpdateClientProfileModalMutation({
      onCompleted: () => {
        onAfterSave?.();
        setIsOpen(false);
        showFeedback(`Updated ${clientProfileTypeDisplay} successfully`, {
          variant: 'success',
        });
      },
      onError: (error: ApolloError) => {
        showFeedback(
          `Encountered an error saving the ${clientProfileTypeDisplay}.  Please try again.`
        );
        reportError('Encountered error when updating client profile', error);
      },
      onQueryUpdated: (query) => {
        return shouldRefetchQuery(query.queryName, {
          ignoredQueryDocuments,
        });
      },
      refetchQueries: 'active',
      awaitRefetchQueries: true,
    });

  const handleRemove = useCallback(async () => {
    if (!clientProfileId) {
      showFeedback(
        `Could not remove the ${clientProfileTypeDisplay}.  Please try again.`
      );
      return;
    }

    await deleteClientProfile({
      variables: {
        clientProfileID: clientProfileId,
      },
    });
  }, [
    clientProfileId,
    showFeedback,
    clientProfileTypeDisplay,
    deleteClientProfile,
  ]);

  const onSave = handleSubmit(async (formData) => {
    if (isCreate) {
      const payload = mapFormToCreatePayload(
        formData,
        householdId,
        isPrimary,
        createdFromSuggestionID
      );
      if (!payload.input) {
        showFeedback(
          `Unable to save the ${clientProfileTypeDisplay}.  Please try again`
        );
        reportError(
          'Could not generate payload to create profile',
          new Error(payload.errorMessage)
        );
        return;
      }
      const output = await createClientProfile({
        variables: {
          input: payload.input,
        },
      });

      trackUserEvent('client_profile created', {
        isGrantor: formData.isGrantor,
        isPrimary: formData.isPrimary,
      });

      return output;
    } else {
      const { variables, errorMessage } = mapFormToUpdatePayload(
        formData,
        householdId,
        clientProfileId
      );
      if (errorMessage) {
        showFeedback(
          `Unable to save the ${clientProfileTypeDisplay}.  Please try again`
        );
        reportError(
          'Could not generate payload to update profile',
          new Error(errorMessage)
        );
        return;
      }
      return updateClientProfile({ variables });
    }
  });

  const isDisabled = dataFetchLoading;
  const isLoading =
    formState.isSubmitting || createLoading || updateLoading || deleteLoading;

  const drawerTitle = useMemo(() => {
    if (isCreate) {
      const indefiniteArticle = getIndefiniteArticle(clientProfileTypeDisplay);
      return `Add ${indefiniteArticle} ${clientProfileTypeDisplay}`;
    }
    return `Edit ${clientProfileTypeDisplay}`;
  }, [clientProfileTypeDisplay, isCreate]);

  const deleteButtonVisible = canEdit && !isCreate && allowDelete;

  return (
    <FormSideDrawer
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      heading={drawerTitle}
      shouldBlockClose={shouldBlockNavigation}
      data-testid="client-profile-modal"
      body={
        <FormProvider {...formMethods}>
          <HouseholdPermissionAwareDisabledProvider forceDisabled={isDisabled}>
            <Stack mt={2}>
              <ClientProfilePerson
                // don't allow untoggling a primary client from being a grantor
                disableGrantor={(isCreate && forceGrantor) || isPrimary}
                hideRelationships={hideRelationshipInput}
              />
            </Stack>
          </HouseholdPermissionAwareDisabledProvider>
        </FormProvider>
      }
      footer={
        <Stack direction="row" spacing={1} width="100%">
          {deleteButtonVisible && (
            <DeleteButton
              contextualHelp={
                <PopperContent
                  body={'Individuals associated with a trust cannot be deleted'}
                />
              }
              loading={deleteLoading}
              onConfirmDelete={handleRemove}
            />
          )}
          {clientProfileId && (
            <MergeClientProfilesAction
              clientProfileId={clientProfileId}
              profiles={
                data?.households?.edges?.[0]?.node?.clientProfiles ?? []
              }
              disabled={isDisabled || isLoading || formState.isDirty}
              onMerged={() => {
                onAfterSave?.();
                setIsOpen(false);
              }}
            />
          )}
          <Box flexGrow={1} />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsOpen(false)}
            data-testid="client-profile-modal-cancel"
          >
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              data-testid="client-profile-modal-save"
              disabled={isDisabled}
              loading={isLoading}
              size="sm"
              variant="primary"
              onClick={onSave}
            >
              {isCreate ? `Add ${clientProfileTypeDisplay}` : 'Save changes'}
            </Button>
          )}
        </Stack>
      }
    />
  );
}

// nulling out the side drawer if not open resolves issues around resetting the state when reopening the side drawer,
// as the default form state is derived from props, but not updated on subsequent renders.
export function ClientProfileSideDrawer(props: ClientProfileSideDrawerProps) {
  if (!props.isOpen) {
    return null;
  }

  return (
    <ClientProfileSideDrawerContextProvider
      householdId={props.householdId}
      clientProfileId={props.clientProfileId}
      isPrimary={props.isPrimary ?? false}
    >
      <ClientProfileSideDrawerInner {...props} />
    </ClientProfileSideDrawerContextProvider>
  );
}
